import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { CANDIDATE_STATUS, EVALUATION_ROUNDS, USER_ROLES } from "@/types/enums";
import {
  formatCandidateStatus,
  formatEvaluationRound,
  formatDateTime,
} from "@/utils/formatters";
import {
  User,
  Mail,
  Phone,
  GraduationCap,
  Briefcase,
  Award,
  MapPin,
  Calendar,
  FileText,
  ExternalLink,
} from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/useAuth";
import { useAuthorization } from "@/hooks/useAuthorization";
import { getFeedbackTemplate } from "@/constants/feedbackTemplates";

type Candidate = Tables<"candidates"> & {
  colleges?: { name: string } | null;
  job_positions?: { role_name: string; job_type?: string } | null;
  evaluation_rounds?: { round_name: string; score: number | null }[];
};
type EvaluationRound = Tables<"evaluation_rounds">;
type CandidateResponse = Tables<"candidate_responses">;

const evaluationSchema = z.object({
  round_name: z.string().min(1, "Round is required"),
  score: z.number().min(10, "Minimum score is 10").max(100),
  feedback: z.string().min(1, "Feedback is required"),
  status: z.string().min(1, "Status is required"),
  candidate_status: z.string().min(1, "Candidate status is required"),
  current_round: z.string().min(1, "Current round is required"),
});

type EvaluationForm = z.infer<typeof evaluationSchema>;

interface CandidateEvaluateDialogProps {
  candidate: Candidate;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export function CandidateEvaluateDialog({
  candidate,
  open,
  onClose,
  onUpdate,
}: CandidateEvaluateDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { role } = useAuthorization();

  const roundOptions =
    role === USER_ROLES.PANELIST
      ? [EVALUATION_ROUNDS.CAMPUS_INTERVIEW, EVALUATION_ROUNDS.OFFICE_TECHNICAL]
      : [
          EVALUATION_ROUNDS.CAMPUS_INTERVIEW,
          EVALUATION_ROUNDS.OFFICE_TECHNICAL,
          EVALUATION_ROUNDS.HR_FOUNDER,
        ];

  const roundStatusOptions = [
    CANDIDATE_STATUS.PASSED,
    CANDIDATE_STATUS.FAILED,
    CANDIDATE_STATUS.HOLD,
  ];

  const form = useForm<EvaluationForm>({
    resolver: zodResolver(evaluationSchema),
    defaultValues: {
      round_name: candidate.current_round || "",
      score: 10,
      feedback: getFeedbackTemplate(candidate.job_positions?.job_type),
      status: "",
      candidate_status: candidate.status,
      current_round: candidate.current_round || "",
    },
  });

  // Fetch evaluation rounds for this candidate
  const { data: evaluationRounds = [] } = useQuery({
    queryKey: ["evaluation-rounds", candidate.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("evaluation_rounds")
        .select(
          `
          *,
          evaluator:evaluator_id(full_name)
        `
        )
        .eq("candidate_id", candidate.id)
        .order("evaluated_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  // Fetch candidate responses grouped by questions
  const { data: candidateResponses = [] } = useQuery({
    queryKey: ["candidate-responses", candidate.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("candidate_responses")
        .select(
          `
          *,
          questions(question_text, question_type, options, correct_answer)
        `
        )
        .eq("candidate_id", candidate.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  // Fetch job position and college info
  const { data: jobPosition } = useQuery({
    queryKey: ["job-position", candidate.job_position_id],
    queryFn: async () => {
      if (!candidate.job_position_id) return null;
      const { data, error } = await supabase
        .from("job_positions")
        .select("*")
        .eq("id", candidate.job_position_id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: open && !!candidate.job_position_id,
  });

  const { data: college } = useQuery({
    queryKey: ["college", candidate.college_id],
    queryFn: async () => {
      if (!candidate.college_id) return null;
      const { data, error } = await supabase
        .from("colleges")
        .select("*")
        .eq("id", candidate.college_id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: open && !!candidate.college_id,
  });

  const evaluationMutation = useMutation({
    mutationFn: async (data: EvaluationForm) => {
      // Insert evaluation round
      const { error: roundError } = await supabase
        .from("evaluation_rounds")
        .insert({
          candidate_id: candidate.id,
          round_name: data.round_name,
          score: data.score,
          feedback: data.feedback,
          status: data.status as any,
          evaluator_id: user?.id ?? null,
        });

      if (roundError) throw roundError;

      // Update candidate status and current round
      const { error: candidateError } = await supabase
        .from("candidates")
        .update({
          status: data.candidate_status as any,
          current_round: data.current_round,
        })
        .eq("id", candidate.id);

      if (candidateError) throw candidateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["candidates"] });
      queryClient.invalidateQueries({
        queryKey: ["evaluation-rounds", candidate.id],
      });
      toast({
        title: "Success",
        description: "Evaluation submitted successfully.",
      });
      form.reset();
      onUpdate();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: EvaluationForm) => {
    evaluationMutation.mutate(data);
  };

  const renderArrayField = (
    data: any,
    emptyMessage: string,
    title: string,
    icon: React.ReactNode
  ) => {
    if (!data)
      return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              {icon}
              {title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{emptyMessage}</p>
          </CardContent>
        </Card>
      );

    try {
      let items: string[] = [];

      if (Array.isArray(data)) {
        items = data.filter(
          (item) => item && typeof item === "string" && item.trim() !== ""
        );
      } else if (typeof data === "string") {
        items = [data];
      }

      if (items.length === 0) {
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                {icon}
                {title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{emptyMessage}</p>
            </CardContent>
          </Card>
        );
      }

      return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              {icon}
              {title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200 shadow-sm"
                >
                  <p className="text-sm leading-relaxed text-blue-900">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      );
    } catch {
      return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              {icon}
              {title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Invalid data format</p>
          </CardContent>
        </Card>
      );
    }
  };

  const renderCandidateInfo = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {candidate.full_name}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="font-medium">Email:</span> {candidate.email}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="font-medium">Phone:</span>{" "}
                {candidate.phone || "N/A"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="font-medium">Position Applied:</span>{" "}
                {jobPosition?.role_name || candidate.position_applied || "N/A"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="font-medium">College:</span>{" "}
                {college?.name || candidate.college_university || "N/A"}
              </div>
            </div>
            <div>
              <span className="font-medium">CGPA:</span>{" "}
              {candidate.cgpa || "N/A"}
            </div>
            <div>
              <span className="font-medium">Branch:</span>{" "}
              {candidate.branch || "N/A"}
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="font-medium">Permanent City:</span>{" "}
                {candidate.permanent_city || "N/A"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="font-medium">Current City:</span>{" "}
                {candidate.current_city || "N/A"}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div>
              <span className="font-medium">Current Status:</span>
              <Badge className="ml-2">
                {formatCandidateStatus(candidate.status)}
              </Badge>
            </div>
            <div>
              <span className="font-medium">Current Round:</span>
              <Badge variant="outline" className="ml-2">
                {formatEvaluationRound(candidate.current_round || "")}
              </Badge>
            </div>
            <div>
              <span className="font-medium">Final Score:</span>
              <Badge variant="secondary" className="ml-2">
                {candidate.final_score != null
                  ? `${candidate.final_score}%`
                  : "N/A"}
              </Badge>
            </div>
          </div>

          {candidate.key_skills && candidate.key_skills.length > 0 && (
            <div>
              <span className="font-medium">Skills:</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {candidate.key_skills.map((skill, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {candidate.permanent_address && (
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
              <div>
                <span className="font-medium">Address:</span>{" "}
                {candidate.permanent_address}
              </div>
            </div>
          )}
          {candidate.current_address && (
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
              <div>
                <span className="font-medium">Current:</span>{" "}
                {candidate.current_address}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Additional information sections */}
      {renderArrayField(
        candidate.projects_details,
        "No projects added",
        "Projects",
        <Briefcase className="h-5 w-5" />
      )}

      {renderArrayField(
        candidate.project_links,
        "No project links added",
        "Project Links",
        <ExternalLink className="h-5 w-5" />
      )}

      {renderArrayField(
        candidate.internships_experience,
        "No experience added",
        "Work Experience & Internships",
        <Briefcase className="h-5 w-5" />
      )}

      {renderArrayField(
        candidate.certifications_awards,
        "No certifications or awards added",
        "Certifications & Awards",
        <Award className="h-5 w-5" />
      )}

      {renderArrayField(
        candidate.event_participations,
        "No event participations added",
        "Event Participations & Hackathons",
        <Calendar className="h-5 w-5" />
      )}
    </div>
  );

  const renderResponses = () => (
    <div className="space-y-4">
      {candidateResponses.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">
              No assessment responses available for this candidate.
            </p>
          </CardContent>
        </Card>
      ) : (
        candidateResponses.map((response) => (
          <Card key={response.id}>
            <CardHeader>
              <CardTitle className="text-base">
                {(response.questions as any)?.question_text}
              </CardTitle>
              <div className="flex gap-2">
                <Badge variant="outline">
                  {(response.questions as any)?.question_type}
                </Badge>
                {response.is_correct !== null && (
                  <Badge
                    variant={response.is_correct ? "default" : "destructive"}
                  >
                    {response.is_correct ? "Correct" : "Incorrect"}
                  </Badge>
                )}
                {response.ai_score && (
                  <Badge variant="secondary">
                    AI Score: {response.ai_score}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {response.selected_option && (
                  <div>
                    <span className="font-medium">Selected Option:</span>{" "}
                    {response.selected_option}
                  </div>
                )}
                {response.response_text && (
                  <div>
                    <span className="font-medium">Response:</span>
                    <div className="mt-1 p-3 bg-muted rounded text-sm whitespace-pre-line">
                      {response.response_text}
                    </div>
                  </div>
                )}
                {(response.questions as any)?.correct_answer && (
                  <div>
                    <span className="font-medium">Correct Answer:</span>
                    <div className="mt-1 p-3 bg-green-50 rounded text-sm">
                      {(response.questions as any).correct_answer}
                    </div>
                  </div>
                )}
                {response.ai_feedback && (
                  <div>
                    <span className="font-medium">AI Feedback:</span>
                    <div className="mt-1 p-3 bg-blue-50 rounded text-sm whitespace-pre-line">
                      {response.ai_feedback}
                    </div>
                  </div>
                )}
                {response.time_taken && (
                  <div className="text-sm text-muted-foreground">
                    Time taken: {response.time_taken} seconds
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );

  const renderEvaluationHistory = () => (
    <div className="space-y-4">
      {evaluationRounds.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">
              No evaluation rounds completed yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        evaluationRounds.map((round) => (
          <Card key={round.id}>
            <CardHeader>
              <CardTitle className="text-base">
                {formatEvaluationRound(round.round_name)}
              </CardTitle>
              <div className="flex gap-2">
                {round.score !== null && (
                  <Badge variant="outline">Score: {round.score}%</Badge>
                )}
                {round.status && (
                  <Badge variant="secondary">
                    {formatCandidateStatus(round.status)}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Evaluated:</span>{" "}
                  {formatDateTime(round.evaluated_at)}
                  {round.evaluator && (
                    <span> by {(round.evaluator as any).full_name}</span>
                  )}
                </div>
                {round.feedback && (
                  <div>
                    <span className="font-medium">Feedback:</span>
                    <div className="mt-1 p-3 bg-muted rounded whitespace-pre-line">
                      {round.feedback}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-6 w-6" />
            <span className="font-semibold text-lg">
              Evaluate Candidate - {candidate.full_name}
            </span>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="info" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="info">Candidate Info</TabsTrigger>
            <TabsTrigger value="responses">Assessment Answers</TabsTrigger>
            <TabsTrigger value="history">Evaluation History</TabsTrigger>
            <TabsTrigger value="evaluate">New Evaluation</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-4">
            {renderCandidateInfo()}
          </TabsContent>

          <TabsContent value="responses" className="space-y-4">
            {renderResponses()}
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            {renderEvaluationHistory()}
          </TabsContent>

          <TabsContent value="evaluate" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Add New Evaluation</CardTitle>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="round_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Evaluation Round</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select round" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {roundOptions.map((round) => (
                                  <SelectItem key={round} value={round}>
                                    {formatEvaluationRound(round)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="score"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Score (10-100)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min="10"
                                max="100"
                                {...field}
                                value={field.value || ""}
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.value
                                      ? parseInt(e.target.value)
                                      : undefined
                                  )
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Round Status</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {roundStatusOptions.map((status) => (
                                  <SelectItem key={status} value={status}>
                                    {formatCandidateStatus(status)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="candidate_status"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Update Candidate Status</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {Object.values(CANDIDATE_STATUS).map(
                                  (status) => (
                                    <SelectItem key={status} value={status}>
                                      {formatCandidateStatus(status)}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="current_round"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Next Round</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select round" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Object.values(EVALUATION_ROUNDS).map((round) => (
                                <SelectItem key={round} value={round}>
                                  {formatEvaluationRound(round)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="feedback"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Feedback</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Enter evaluation feedback..."
                              className="min-h-[100px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={evaluationMutation.isPending}
                      >
                        {evaluationMutation.isPending
                          ? "Submitting..."
                          : "Submit Evaluation"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
